import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Receipt, Users, Wallet, BarChart3, Settings, LogOut, Menu, Armchair, ChefHat, BookOpen, ArrowLeftRight, Printer } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/pos/context/AuthContext";
import { api } from "@/pos/lib/api";
import { getPrinter, getRole } from "@/pos/lib/printer";
import { printOnline, printKitchenAuto, loadKitchenMode } from "@/pos/lib/print";
import { beep } from "@/pos/lib/restaurant";
import PrinterBadge from "@/pos/components/PrinterBadge";
import NewOrderAlert from "@/pos/components/NewOrderAlert";

const NAV = [
  { to: "/pos", label: "Utama", icon: LayoutDashboard, id: "dashboard" },
  { to: "/pos/order", label: "Ambil Order", icon: ShoppingCart, id: "pos" },
  { to: "/pos/meja", label: "Pengurusan Meja", icon: Armchair, id: "tables" },
  { to: "/pos/dapur", label: "Paparan Dapur", icon: ChefHat, id: "kitchen" },
  { to: "/pos/jualan", label: "Senarai Jualan", icon: Receipt, id: "sales" },
  { to: "/panel/menu", label: "Menu (Panel)", icon: BookOpen, id: "menu", owner: true },
  { to: "/pos/pelanggan", label: "Pelanggan & Hutang", icon: Users, id: "debtors" },
  { to: "/pos/tunai", label: "Buku Tunai", icon: Wallet, id: "cashbook", owner: true },
  { to: "/pos/laporan", label: "Laporan POS", icon: BarChart3, id: "reports", owner: true },
  { to: "/pos/tetapan", label: "Tetapan & Printer", icon: Settings, id: "settings" },
];

const BOTTOM = [
  { to: "/pos", label: "Utama", icon: LayoutDashboard },
  { to: "/pos/order", label: "Order", icon: ShoppingCart },
  { to: "/pos/meja", label: "Meja", icon: Armchair },
  { to: "/pos/dapur", label: "Dapur", icon: ChefHat },
];

const ROLE = { owner: "Pemilik", cashier: "Juruwang" };

function useAutoPrint() {
  const busy = useRef(false);
  const warned = useRef(new Set());
  useEffect(() => {
    const tick = async () => {
      if (busy.current || !getPrinter().autoOnline) return;
      busy.current = true;
      try {
        const { data } = await api.get("/print-queue");
        for (const o of data) {
          const { data: c } = await api.post(`/orders/${o.id}/print-claim`);
          if (!c.claimed) continue;
          beep();
          const how = await printOnline(o, { socketOnly: true });
          if (how !== "failed") { toast.success(`Pesanan app ${o.order_no} masuk – resit dicetak`); continue; }
          await api.post(`/orders/${o.id}/print-release`);
          if (warned.current.has(o.id)) continue;
          warned.current.add(o.id);
          const why = getPrinter().method === "direct" ? "Printer Bluetooth tidak bersambung" : "RawBT WebSocket tidak aktif";
          toast.warning(`Pesanan app ${o.order_no} masuk. ${why} – tekan Cetak.`, {
            duration: Infinity,
            action: { label: "Cetak", onClick: async () => { const r = await api.post(`/orders/${o.id}/print-claim`); if (r.data.claimed) printOnline(o); } },
          });
        }
      } catch (e) { /* cuba lagi pada kitaran seterusnya */ } finally { busy.current = false; }
    };
    tick();
    const t = setInterval(tick, 8000);
    return () => clearInterval(t);
  }, []);
}

// Tablet dapur (mod 2 tablet): tiket dapur dicetak sendiri bila order masuk.
function useKitchenTabletPrint() {
  const busy = useRef(false);
  const warned = useRef(false);
  useEffect(() => {
    const tick = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        if ((await loadKitchenMode()) !== "tablet" || getRole() !== "dapur") return;
        const { data } = await api.get("/kitchen-queue");
        for (const o of data) {
          const { data: c } = await api.post(`/orders/${o.id}/kitchen-claim`);
          if (!c.claimed) continue;
          beep();
          if ((await printKitchenAuto(o)) !== "failed") { toast.success(`Tiket dapur #${o.number} dicetak`); warned.current = false; continue; }
          await api.post(`/orders/${o.id}/kitchen-release`);
          if (!warned.current) toast.error("Printer dapur tidak bersambung – tiket dapur belum dicetak. Semak Tetapan & Printer.", { duration: 10000 });
          warned.current = true;
          break;
        }
      } catch (e) { /* cuba lagi */ } finally { busy.current = false; }
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => clearInterval(t);
  }, []);
}

function NavList({ onNav }) {
  const { isOwner, user, logout } = useAuth();
  const items = NAV.filter((n) => !n.owner || isOwner);
  const auto = getPrinter().autoOnline;
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-6 pt-6">
        <div className="flex items-center gap-2.5">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-white p-1"><img src="/img/logo.png" alt="POKYA" className="h-full w-full object-contain" /></div>
          <div>
            <div className="font-heading text-lg font-extrabold leading-none text-white">POKYA POS</div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-red-200/60">Restaurant</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === "/pos"} onClick={onNav} data-testid={`nav-${n.id}`}
            className={({ isActive }) => `nav-item ${isActive ? "nav-item-active" : ""}`}>
            <n.icon size={18} strokeWidth={2} />
            <span>{n.label}</span>
          </NavLink>
        ))}
        <NavLink to="/panel" className="nav-item" data-testid="nav-back-panel"><ArrowLeftRight size={18} /><span>Panel Pesanan App</span></NavLink>
      </nav>
      <div className="border-t border-white/10 p-4">
        <PrinterBadge />
        <div className={`mb-3 flex items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] ${auto ? "bg-red-500/20 text-red-200" : "bg-white/5 text-slate-400"}`} data-testid="autoprint-indicator">
          <Printer size={13} /> Auto-cetak pesanan app: {auto ? "HIDUP" : "MATI"}
        </div>
        <div className="text-sm font-semibold text-white" data-testid="current-user-name">{user?.name}</div>
        <div className="truncate text-xs text-red-100/50">{user?.email}</div>
        <div className="mt-1 inline-block rounded bg-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-300" data-testid="current-user-role">
          {ROLE[user?.role] || user?.role}
        </div>
        <button onClick={logout} data-testid="logout-btn" className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-50/70 transition-colors hover:bg-white/10 hover:text-white">
          <LogOut size={16} /> Log Keluar
        </button>
      </div>
    </div>
  );
}

function BottomNav({ onMenu }) {
  const item = "flex flex-1 flex-col items-center gap-0.5 rounded-full py-1.5 text-[11px] font-medium transition-colors";
  return (
    <nav className="bottom-nav fixed inset-x-4 bottom-3 z-30 flex items-center rounded-full px-2 py-1.5 text-white lg:hidden" data-testid="bottom-nav">
      {BOTTOM.map((b) => (
        <NavLink key={b.to} to={b.to} end={b.to === "/pos"} data-testid={`bottom-nav-${b.label.toLowerCase()}`}
          className={({ isActive }) => `${item} ${isActive ? "bg-white/20 text-white" : "text-red-50/80"}`}>
          <b.icon size={19} />{b.label}
        </NavLink>
      ))}
      <button onClick={onMenu} className={`${item} text-red-50/80`} data-testid="mobile-menu-btn"><Menu size={19} />Menu</button>
    </nav>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  useAutoPrint();
  useKitchenTabletPrint();
  return (
    <div className="app-bg min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-[#2A0707] lg:block">
        <NavList />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 border-0 bg-[#2A0707] p-0 [&>button]:text-white">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <NavList onNav={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="pos-main pb-24 lg:pb-0 lg:pl-64">
        <Outlet />
      </main>
      <BottomNav onMenu={() => setOpen(true)} />
      <div className="pos-printer-float fixed bottom-[148px] right-4 z-40 lg:hidden"><PrinterBadge compact /></div>
      <NewOrderAlert />
    </div>
  );
}
